import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import {
  Box,
  Button,
  ButtonProps,
  Drawer,
  Hidden,
  IconButton,
  Theme,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Add as AddIcon, Menu as MenuIcon } from "@mui/icons-material";
import { useFormikContext } from "formik";

import { NeedHelp } from "components/evaluate-panel-options/Sidebar/NeedHelp";
import { Para14Text } from "components/shared/Typography";
import UploadTargetsBtn from "components/shared/UploadTargetsBtn";
import LeftContent from "components/wrappers/LeftContent";
import { KEY_METRICS } from "helpers/apis";
import useAxios from "helpers/axios";
import useAuth from "hooks/useAuth";
import useCatalogDetail from "hooks/useCatalogDetail";
import useDialog from "hooks/useDialog";
import { DesignType, IDesign, PlatformType } from "types/design";
import { ICustomPanelForm } from "types/form";

import { CancelButton } from "./CancelButton";
import { CompareConfirmAlert } from "./CompareConfirmAlert";
import { CustomizeButton } from "./CustomizeButton";
import { QuoteRequestButton } from "./QuoteRequestButton";
import { SaveDraftButton } from "./SaveDraftButton";
import { SelectPlatformButton } from "./SelectPlatformButton";

const useStyles = makeStyles((theme) => ({
  mobileHeader: {
    position: "relative",
    margin: "-8px",
    width: "calc(100% + 32px)",
  },
  toolbar: {
    width: "100%",
  },
}));

const CompareButton = (props: ButtonProps) => (
  <Button startIcon={<AddIcon />} fullWidth type="button" {...props}>
    Compare
  </Button>
);

export const PanelSidebar = () => {
  const { setOpenLoginDialog } = useDialog();
  const { isLoggedIn } = useAuth();
  const { pathname } = useLocation();
  let {
    catalog,
    customize,
    diseaseType,
    draftSaved,
    foundGenes,
    isCustomPanel,
    isDraftCustomPanel,
    isSuggestedPanel,
    newDetails,
    setCatalog,
    workflow: workflowObj,
  } = useCatalogDetail();

  const [expanded, setExpanded] = useState<boolean>(false);
  const [isEvaluation, setIsEvaluation] = useState<boolean>(false);
  const [quoteError, setQuoteError] = useState<boolean>(false);
  const [requisitionId, setRequisitionId] = useState<string>();
  catalog = catalog as IDesign;
  const [platform, setPlatform] = useState<PlatformType>();
  const [openCompareConfirmDialog, setOpenCompareConfirmDialog] =
    useState<boolean>(false);

  const classes = useStyles();
  const { errors, handleSubmit, isSubmitting, values } =
    useFormikContext<ICustomPanelForm>();
  const isCatalogPanel = catalog.designType === DesignType.CATALOG_PANEL;
  const isQuotable = isCatalogPanel || isCustomPanel;
  const { fetchData } = useAxios({
    config: {
      url: KEY_METRICS,
    },
    callOnInit: false,
  });

  // Need to update key metrics whenever added panels are changed
  useEffect(() => {
    if (customize || isDraftCustomPanel) {
      const panelspresent = [
        ...(isSuggestedPanel || isCustomPanel ? [] : [catalog?.id]),
        ...newDetails.keys(),
      ];

      if (panelspresent.length > 0) {
        fetchData(
          {
            params: {
              panelspresent: panelspresent.join(),
              workflow: workflowObj?.id,
              diseaseType: diseaseType?.id,
              hasSnpId: values?.snpId,
              hasMsi: values?.msi,
              targets: foundGenes,
            },
          },
          ({ response }: any) => {
            if (response.data && typeof response.data === "object") {
              setCatalog((catalog: IDesign) => ({
                ...catalog,
                ...response.data,
              }));
            }
          }
        );
      } else {
        setCatalog((catalog: IDesign) => ({
          ...catalog,
          numberGenes: 0,
          totalBases: 0,
          readsRecommended: 0,
          numberPrimerPairs: 0,
        }));
      }
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [
    [...newDetails.keys()].length,
    isSuggestedPanel,
    isCustomPanel,
    catalog?.id,
    values?.msi,
    values?.snpId,
    customize,
    isDraftCustomPanel,
  ]);

  useEffect(() => {
    if (pathname.indexOf("/evaluate-panel-options") === 0) {
      setIsEvaluation(true);
      if (catalog?.requisitionId) {
        setRequisitionId(catalog?.requisitionId);
      }
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [pathname]);

  useEffect(() => {
    if (!!platform) {
      setQuoteError(false);
    }
  }, [platform]);

  useEffect(() => {
    const drawerEl = document.getElementById("panelSidebarDrawer");
    const toolbarEl = document.getElementById("panelSidebarToolbar");

    if (!!drawerEl && !!toolbarEl) {
      const top = toolbarEl.getBoundingClientRect().bottom || 0;
      drawerEl.style.top = `${top}px`;

      (
        document.querySelector(
          "#panelSidebarDrawer .MuiBackdrop-root"
        ) as HTMLElement
      ).style.top = `${top}px`;
      (
        document.querySelector(
          "#panelSidebarDrawer .MuiDrawer-paper"
        ) as HTMLElement
      ).style.top = `${top}px`;
    }
  }, [
    !!document.getElementById("panelSidebarDrawer"),
    document.getElementById("panelSidebarToolbar")?.getBoundingClientRect()
      .bottom,
  ]);

  const isDirtyAfterDrafting = useMemo(() => {
    if (!draftSaved) {
      return false;
    }
    // For non suggest panel like catalog panel and custom panel, it includes panel itself in the panelspresent.
    if (
      values.panelName !== draftSaved.name ||
      (values.msi || false) !== draftSaved.hasMsi ||
      (values.snpId || false) !== draftSaved.hasSnpId ||
      Array.from(newDetails).length !==
        draftSaved.panelspresent.length -
          (isSuggestedPanel || isCustomPanel ? 0 : 1)
    ) {
      return true;
    }
    if (Array.from(newDetails).length) {
      const diffPanels = Array.from(newDetails).filter((newDetail, index) => {
        const matchedPanel =
          draftSaved!.panelspresent[
            isSuggestedPanel || isCustomPanel ? index : index + 1
          ];
        return (
          matchedPanel.cnv !== newDetail[1].cnv ||
          matchedPanel.design !== newDetail[0]
        );
      });
      if (diffPanels.length) {
        return true;
      }
    }
    return false;
  }, [values, newDetails, draftSaved]);

  const onCompareButtonAction = () => {
    if (isEvaluation && draftSaved?.id && isDirtyAfterDrafting) {
      setOpenCompareConfirmDialog(true);
    } else {
      handleSubmit();
    }
  };

  const QuoteError = () => {
    return quoteError ? (
      <Para14Text color="error">
        You must select a platform before requesting a quote
      </Para14Text>
    ) : null;
  };

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  return (
    <Box>
      <Hidden mdUp>
        <Toolbar
          variant="dense"
          disableGutters
          className={classes.toolbar}
          id="panelSidebarToolbar"
        >
          <LeftContent className={classes.mobileHeader}>
            <Box width={1}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems={isMobile ? "flex-start" : "center"}
                width={1}
              >
                <IconButton
                  edge="start"
                  color="primary"
                  onClick={() => setExpanded(!expanded)}
                  size="large"
                >
                  <MenuIcon />
                </IconButton>
                {isLoggedIn && (
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    flexWrap={isMobile ? "wrap" : undefined}
                    mt={isMobile ? 1 : undefined}
                    ml={isMobile ? -3 : undefined}
                    width={1}
                    columnGap={1}
                  >
                    {(!customize || !isEvaluation) &&
                      !isSuggestedPanel &&
                      (isQuotable ? (
                        <>
                          <SelectPlatformButton
                            isEvaluation={isEvaluation}
                            platform={platform}
                            requisitionId={requisitionId}
                            setPlatform={setPlatform}
                          />
                          <QuoteRequestButton
                            platform={platform}
                            requisitionId={requisitionId}
                            setRequisitionId={setRequisitionId}
                            setQuoteError={setQuoteError}
                          />
                        </>
                      ) : (
                        <UploadTargetsBtn variant="outlined" />
                      ))}
                    {customize && (
                      <>
                        {isEvaluation && (
                          <CompareButton
                            size="small"
                            fullWidth={false}
                            disabled={isSubmitting}
                            onClick={onCompareButtonAction}
                          />
                        )}
                        <SaveDraftButton />
                        <CancelButton isEvaluation={isEvaluation} />
                      </>
                    )}

                    {!customize &&
                      (isEvaluation ||
                        isDraftCustomPanel ||
                        isCatalogPanel) && (
                        <CustomizeButton
                          disabled={!isCatalogPanel && !!requisitionId}
                          isEvaluation={isEvaluation}
                        />
                      )}
                  </Box>
                )}
              </Box>
              {isQuotable && (
                <Box textAlign="right">
                  <QuoteError />
                </Box>
              )}
            </Box>
          </LeftContent>
        </Toolbar>
        <Drawer
          id="panelSidebarDrawer"
          open={expanded}
          keepMounted
          onClose={() => setExpanded(false)}
        >
          <Box padding={3} width={338}>
            <NeedHelp />
          </Box>
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <LeftContent>
          <Box
            position="sticky"
            top="0"
            display="flex"
            flexDirection="column"
            rowGap={2}
          >
            {!isLoggedIn && (
              <>
                <Typography>
                  To request quote or customize panel, please sign in.
                </Typography>
                <Button fullWidth onClick={() => setOpenLoginDialog(true)}>
                  Sign In
                </Button>
              </>
            )}
            {isLoggedIn && (
              <>
                {(!customize || !isEvaluation) &&
                  !isSuggestedPanel &&
                  (isQuotable ? (
                    <>
                      <SelectPlatformButton
                        isEvaluation={isEvaluation}
                        platform={platform}
                        requisitionId={requisitionId}
                        setPlatform={setPlatform}
                      />
                      <QuoteRequestButton
                        platform={platform}
                        requisitionId={requisitionId}
                        setRequisitionId={setRequisitionId}
                        setQuoteError={setQuoteError}
                      />
                      <QuoteError />
                    </>
                  ) : (
                    <UploadTargetsBtn
                      fullWidth
                      size="medium"
                      variant="outlined"
                    />
                  ))}
                {(customize || isDraftCustomPanel) && errors.panels && (
                  <Box order={-1}>
                    <Typography variant="h5" align="center" color="error">
                      {errors.panels}
                    </Typography>
                  </Box>
                )}
                {customize && (
                  <>
                    {isEvaluation && (
                      <CompareButton
                        disabled={isSubmitting}
                        onClick={onCompareButtonAction}
                      />
                    )}
                    <SaveDraftButton />
                    <CancelButton isEvaluation={isEvaluation} />
                  </>
                )}
                {!customize &&
                  (isEvaluation || isDraftCustomPanel || isCatalogPanel) && (
                    <CustomizeButton
                      disabled={!isCatalogPanel && !!requisitionId}
                      isEvaluation={isEvaluation}
                    />
                  )}
              </>
            )}
            <Box mt={isEvaluation ? 5 : 3}>
              <NeedHelp />
            </Box>
          </Box>
        </LeftContent>
      </Hidden>

      <CompareConfirmAlert
        open={openCompareConfirmDialog}
        setOpen={setOpenCompareConfirmDialog}
      />
    </Box>
  );
};
